/**
 * Cycle-safe replacement for react-query's built-in structural sharing.
 *
 * Our API client (@services/api) deserializes JSON:API payloads with Jsona,
 * which wires relationships up as real object references. Those graphs are
 * often cyclical (program → item-programs → program, …). React-query's
 * default `replaceEqualDeep` recurses infinitely on cycles, which is why
 * structural sharing used to be disabled globally.
 *
 * This version keeps the behavior of `replaceEqualDeep` — return the previous
 * reference for any subtree that is deeply equal to the fresh result, so
 * memoized selectors / effect deps stay stable across refetches — while
 * tracking visited nodes so cyclical and shared references are safe.
 */

const isSharable = (value: unknown): value is Record<string, unknown> => {
  if (Array.isArray(value)) return true;
  if (typeof value !== "object" || value === null) return false;
  const prototype = Object.getPrototypeOf(value);
  // Only plain objects: Dates, Maps, class instances etc. compare by identity
  return prototype === Object.prototype || prototype === null;
};

type PairCache = WeakMap<object, WeakMap<object, boolean | "pending">>;

/**
 * Deep equality that treats cyclical structures like rational trees: when a
 * pair of nodes is encountered again while it is still being compared, the
 * pair is assumed equal — if everything else matches, the cycles match.
 */
const cyclicEquals = (a: unknown, b: unknown, pairs: PairCache): boolean => {
  if (a === b) return true;
  if (a instanceof Date && b instanceof Date)
    return a.getTime() === b.getTime();
  if (!isSharable(a) || !isSharable(b)) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b) && a.length !== b.length)
    return false;

  let cacheForA = pairs.get(a);
  const cached = cacheForA?.get(b);
  if (cached !== undefined) return cached === "pending" ? true : cached;
  if (!cacheForA) {
    cacheForA = new WeakMap();
    pairs.set(a, cacheForA);
  }
  cacheForA.set(b, "pending");

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  const equal =
    aKeys.length === bKeys.length &&
    bKeys.every((key) => key in a && cyclicEquals(a[key], b[key], pairs));

  cacheForA.set(b, equal);
  return equal;
};

/**
 * Rebuild `next`, swapping in subtrees of `prev` wherever they are deeply
 * equal. `memo` maps nodes of `next` to their rebuilt counterpart so shared
 * references and cycles in `next` are preserved in the result.
 */
const rebuild = (
  prev: unknown,
  next: unknown,
  pairs: PairCache,
  memo: WeakMap<object, unknown>
): unknown => {
  if (cyclicEquals(prev, next, pairs)) return prev;
  if (!isSharable(next)) return next;
  if (memo.has(next)) return memo.get(next);

  const prevObj = isSharable(prev) ? prev : undefined;
  const copy: object = Array.isArray(next)
    ? new Array<unknown>(next.length)
    : {};
  memo.set(next, copy);
  for (const key of Object.keys(next)) {
    Reflect.set(copy, key, rebuild(prevObj?.[key], next[key], pairs, memo));
  }
  return copy;
};

const safeStructuralSharing = <T>(oldData: T | undefined, newData: T): T => {
  if (oldData === undefined) return newData;
  return rebuild(oldData, newData, new WeakMap(), new WeakMap()) as T;
};

export default safeStructuralSharing;
